import type { ScoringRules } from '@/hooks/useLeagueConfig'

export type PickResult = 'win' | 'draw' | 'loss'

export type SeasonHalf = 'apertura' | 'clausura'

/** Compare mode: 'none', 'autopick', or a manager user ID */
export type CompareMode = 'none' | 'autopick' | string

export interface TddHalfStatus {
  used: number
  total: number
  /** Matchday numbers where TDD was used */
  matchdays: number[]
}

export interface TddSummary {
  apertura: TddHalfStatus
  clausura: TddHalfStatus
}

export interface PickEntry {
  matchday: number
  seasonHalf: SeasonHalf
  teamName: string
  teamLogo: string
  result: PickResult | null
  /** Final points (after TDD multiplier, GD bonus, red card penalty) */
  points: number
  /** Base points before modifiers */
  basePoints: number
  gdBonus: number
  redCardPenalty: number
  tddUsed: boolean
  isAutopick: boolean
  isForfeit: boolean
  /** Whether the result has been processed */
  processed: boolean
  /** Whether the picked team was home (true) or away (false) */
  isHome: boolean | null
  /** Opponent team name */
  opponentName: string | null
  /** Score from the picked team's perspective { picked, opponent } */
  score: { picked: number; opponent: number } | null
  /** Kickoff time for pending matches */
  kickoffTime: string | null
}

export interface ManagerInfo {
  id: string
  displayName: string
  initials: string
  rank: number
  points: number
}

/**
 * Derive points from a score using league scoring rules.
 * Used for unprocessed picks where the backend hasn't calculated points yet.
 */
export function derivePointsFromScore(
  score: { picked: number; opponent: number } | null,
  tddUsed: boolean,
  rules: ScoringRules,
): { result: PickResult | null; points: number } {
  if (!score) return { result: null, points: 0 }

  const result: PickResult =
    score.picked > score.opponent ? 'win'
    : score.picked < score.opponent ? 'loss'
    : 'draw'

  const basePoints = result === 'win' ? rules.win
    : result === 'draw' ? rules.draw
    : rules.loss

  const goalDiff = Math.abs(score.picked - score.opponent)
  const gdBonus = result === 'win' && goalDiff >= 3
    ? Math.floor(goalDiff / 3) * rules.win_by_3_plus_bonus
    : 0

  const total = basePoints + gdBonus
  const points = tddUsed ? total * 2 : total

  return { result, points }
}

export interface ManagerPickEntry {
  matchday: number
  teamName: string
  teamLogo: string
  result: PickResult | null
  points: number
  tddUsed: boolean
  /** Whether the picked team was home (true) or away (false) */
  isHome: boolean | null
  /** Opponent team name */
  opponentName: string | null
  /** Score from the picked team's perspective { picked, opponent } */
  score: { picked: number; opponent: number } | null
}
